/**
 * DocsPageView - Pure UI component for documentation page
 *
 * This is the View layer that handles only presentation.
 * Contains no logic, hooks, or side effects.
 */
import type { DocumentationConfig } from './documentation.schema';
import './DocsPageView.css';

interface DocsPageViewProps {
  documentation: DocumentationConfig | null;
  loading: boolean;
  error: string | null;
}

function DocsPageView({ documentation, loading, error }: DocsPageViewProps) {
  if (loading) {
    return (
      <main className="docs-page-view-container" aria-live="polite">
        <div className="docs-page-view-loading" role="status">
          <div className="docs-page-view-loading-text">
            Loading documentation...
          </div>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="docs-page-view-container" aria-live="assertive">
        <div className="docs-page-view-error" role="alert">
          <div className="docs-page-view-error-text">
            Error loading documentation
          </div>
          <div>{error}</div>
        </div>
      </main>
    );
  }

  if (!documentation) {
    return (
      <main className="docs-page-view-container">
        <div className="docs-page-view-no-docs">
          <div className="docs-page-view-no-docs-text">
            No documentation available
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="docs-page-view-container">
      <h1 className="docs-page-view-title">API Documentation</h1>
      <div className="docs-page-view-description">
        <p>
          {documentation.description}
        </p>
      </div>
      
      <div className="docs-page-view-grid">
        {documentation.sections.map((section, index) => (
          <div key={index} className="docs-page-view-section">
            <h2 className="docs-page-view-section-title">
              {section.title}
            </h2>
            <p className="docs-page-view-section-description">
              {section.description}
            </p>
            
            <div className="docs-page-view-endpoint-grid">
              {section.endpoints.map((endpoint, endpointIndex) => (
                <div key={endpointIndex} className="docs-page-view-endpoint">
                  <div className="docs-page-view-endpoint-header">
                    <span className={`docs-page-view-endpoint-method docs-page-view-endpoint-method-${endpoint.method.toLowerCase()}`}>
                      {endpoint.method}
                    </span>
                    <code className="docs-page-view-endpoint-path">
                      {endpoint.path}
                    </code>
                  </div>
                  <p className="docs-page-view-endpoint-description">
                    {endpoint.description}
                  </p>
                  {endpoint.example && (
                    <div className="docs-page-view-endpoint-example">
                      <h4>Example:</h4>
                      <pre>
                        {endpoint.example}
                      </pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </main>
  );
}

export default DocsPageView;